import { apiFetch } from '@/lib/api'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, Zap, Info, Globe, UserPlus, X, Trash2, AlertTriangle, Check, Loader2, XCircle } from 'lucide-react'
import pb from '@/lib/pb'
import { COLLECTIONS } from '@/types'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { MilestoneProgress } from '@/components/logs/MilestoneProgress'
import { ProvisionLogViewer } from '@/components/logs/ProvisionLogViewer'
import { useRegistryVersions } from '@/hooks/useRegistryVersions'
import { useProvisionLogsByTenant } from '@/hooks/useProvisionLogs'
import { toSlug } from '@/lib/utils'

const step1Schema = z.object({
  legal_name: z.string().min(1, 'Legal name is required').max(100),
  slug: z.string().min(4, 'Slug must be at least 4 characters').max(30, 'Slug must be 30 characters or fewer').regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Slug must be lowercase alphanumeric with hyphens'),
})

const step2Schema = z.object({
  primary_contact_name: z.string().min(1, 'Contact name is required'),
  primary_contact_email: z.string().email('Valid email is required'),
  primary_contact_phone: z.string().min(10, 'Enter a 10-digit phone number').max(10, 'Enter a 10-digit phone number'),
  website: z.string().optional(),
  industry: z.string().optional(),
})

type AuthMode = 'domain' | 'user'

const step3AuthSchema = z.object({
  auth_mode: z.enum(['domain', 'user']),
  allowed_domain: z.string().optional(),
  invited_emails: z.array(z.string().email()).optional(),
})

const step4Schema = z.object({
  web_app_version: z.string().min(1, 'Web App version is required'),
  openclaw_version: z.string().min(1, 'OpenClaw version is required'),
})

type Step1Data = z.infer<typeof step1Schema>
type Step2Data = z.infer<typeof step2Schema>
type Step3AuthData = z.infer<typeof step3AuthSchema>
type Step4Data = z.infer<typeof step4Schema>

interface ProvisionWizardProps {
  isOpen: boolean
  onClose: () => void
  onComplete?: () => void
}

const STEP_LABELS = ['Identity', 'Contact', 'Login', 'Versions', 'Provisioning'] as const

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)',
  border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)',
  fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', outline: 'none',
}

function FormField({ label, required, hint, error, children }: { label: string; required?: boolean; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
        {label}{required && <span style={{ color: 'var(--status-unhealthy)' }}>*</span>}
      </label>
      {hint && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{hint}</span>}
      {children}
      {error && <span style={{ fontSize: '11px', color: 'var(--status-unhealthy)', marginTop: '2px' }}>{error}</span>}
    </div>
  )
}

export function ProvisionWizard({ isOpen, onClose, onComplete }: ProvisionWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [slugManual, setSlugManual] = useState(false)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [provisionId, setProvisionId] = useState<string | null>(null)
  const [provisionStartedAt, setProvisionStartedAt] = useState<string | null>(null)
  const [provisionCompletedAt, setProvisionCompletedAt] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTearingDown, setIsTearingDown] = useState(false)

  // Enabled across the whole wizard (not just step 4), so the submit handler
  // on step 5 can resolve real DOCR tags rather than fall through to the
  // hardcoded fallbacks below.
  const { data: versions, isLoading: versionsLoading } = useRegistryVersions(true)
  const { data: logs = [] } = useProvisionLogsByTenant(tenantId)

  const [authMode, setAuthMode] = useState<AuthMode>('domain')
  const [allowedDomain, setAllowedDomain] = useState('')
  const [invitedEmails, setInvitedEmails] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState('')
  const [skipOnboarding, setSkipOnboarding] = useState(false)
  const [openrouterBudget, setOpenrouterBudget] = useState<number>(200)
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')

  const step1Form = useForm<Step1Data>({ resolver: zodResolver(step1Schema), defaultValues: { legal_name: '', slug: '' } })
  const step2Form = useForm<Step2Data>({ resolver: zodResolver(step2Schema), defaultValues: { primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '', website: '', industry: '' } })
  const step4Form = useForm<Step4Data>({ resolver: zodResolver(step4Schema), defaultValues: { web_app_version: '', openclaw_version: '' } })

  const watchedName = step1Form.watch('legal_name')
  useEffect(() => { if (!slugManual && watchedName) step1Form.setValue('slug', toSlug(watchedName), { shouldValidate: true }) }, [watchedName, slugManual, step1Form])

  const watchedSlug = step1Form.watch('slug')
  useEffect(() => {
    const slugRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/
    if (!watchedSlug || watchedSlug.length < 4 || !slugRegex.test(watchedSlug)) {
      setSlugStatus('idle')
      return
    }
    setSlugStatus('checking')
    const timeout = setTimeout(async () => {
      try {
        const result = await pb.collection(COLLECTIONS.TENANTS).getList(1, 1, { filter: `slug = "${watchedSlug}" && is_deleted != true` })
        setSlugStatus(result.totalItems === 0 ? 'available' : 'taken')
      } catch {
        setSlugStatus('idle')
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [watchedSlug])

  useEffect(() => {
    if (versions) {
      if (versions.webApp.length > 0 && !step4Form.getValues('web_app_version')) step4Form.setValue('web_app_version', versions.webApp[0])
      if (versions.openclaw.length > 0 && !step4Form.getValues('openclaw_version')) step4Form.setValue('openclaw_version', versions.openclaw[0])
    }
  }, [versions, step4Form])

  const addInvitedEmail = () => {
    const email = emailInput.trim().toLowerCase()
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !invitedEmails.includes(email)) {
      setInvitedEmails(prev => [...prev, email])
      setEmailInput('')
    }
  }

  const removeInvitedEmail = (email: string) => {
    setInvitedEmails(prev => prev.filter(e => e !== email))
  }

  const isProvisionComplete = useMemo(() => logs.some(l => l.milestone === 'provision_complete' && l.milestone_ended), [logs])
  const isProvisionFailed = useMemo(() => logs.some(l => l.level === 'error' && l.message?.toLowerCase().includes('failed')), [logs])

  useEffect(() => {
    if (isProvisionComplete && !provisionCompletedAt) {
      const lastLog = logs[logs.length - 1]
      setProvisionCompletedAt(lastLog?.timestamp ?? new Date().toISOString())
      toast.success('Tenant provisioned successfully')
      onComplete?.()
    }
  }, [isProvisionComplete, provisionCompletedAt, logs, onComplete])

  useEffect(() => {
    if (isOpen) { setCurrentStep(1); setSlugManual(false); setTenantId(null); setProvisionId(null); setProvisionStartedAt(null); setProvisionCompletedAt(null); setIsSubmitting(false); setIsTearingDown(false); setAuthMode('domain'); setAllowedDomain(''); setInvitedEmails([]); setEmailInput(''); setSkipOnboarding(false); setOpenrouterBudget(200); setSlugStatus('idle'); step1Form.reset(); step2Form.reset(); step4Form.reset() }
  }, [isOpen, step1Form, step2Form, step4Form])

  const startProvisioning = async () => {
    setIsSubmitting(true)
    try {
      const identity = step1Form.getValues()
      const contact = step2Form.getValues()
      const versionData = step4Form.getValues()

      // Hard-fail BEFORE creating the tenant if registry hasn't given us a tag
      // for every required image. Better to refuse than to ship a stale fallback.
      const missing: string[] = []
      if (!versionData.web_app_version) missing.push('web app')
      if (!versionData.openclaw_version) missing.push('OpenClaw')
      if (!versions?.pocketbase?.[0]) missing.push('PocketBase')
      if (!versions?.caddy?.[0]) missing.push('Caddy')
      if (!versions?.n8n?.[0]) missing.push('n8n')
      if (missing.length > 0) {
        setIsSubmitting(false)
        alert(`Cannot start provision — registry has no tag for: ${missing.join(', ')}.\n\nThis usually means /api/registry/versions failed. Check Zeus logs and retry.`)
        return
      }

      const authConfig = {
        auth_mode: authMode,
        allowed_domain: authMode === 'domain' ? allowedDomain : undefined,
        invited_emails: authMode === 'user' ? invitedEmails : undefined,
      }
      const createRes = await apiFetch('/api/tenants', {
        method: 'POST',
        body: JSON.stringify({ name: identity.legal_name, legal_name: identity.legal_name, slug: identity.slug, subdomain: `${identity.slug}.deconstraint.ai`, primary_contact_name: contact.primary_contact_name, primary_contact_email: contact.primary_contact_email, primary_contact_phone: contact.primary_contact_phone, website: contact.website || undefined, industry: contact.industry || undefined, web_app_version: versionData.web_app_version, openclaw_version: versionData.openclaw_version, ...authConfig }),
      })
      if (!createRes.ok) { const err = await createRes.json().catch(() => ({ message: 'Failed to create tenant' })); throw new Error(err.message ?? `Create tenant failed: ${createRes.status}`) }
      const tenant = await createRes.json()
      setTenantId(tenant.id)
      // Send the entire wizard payload — provisioner picks what it needs
      const wizard = {
        identity: {
          legal_name: identity.legal_name,
          slug: identity.slug,
        },
        contact: {
          name: contact.primary_contact_name,
          email: contact.primary_contact_email,
          phone: contact.primary_contact_phone,
          website: contact.website || undefined,
          industry: contact.industry || undefined,
        },
        auth: {
          mode: authMode,
          allowed_domain: authMode === 'domain' ? allowedDomain : undefined,
          invited_emails: authMode === 'user' ? invitedEmails : undefined,
        },
        versions: {
          web_app: versionData.web_app_version,
          openclaw: versionData.openclaw_version,
          pocketbase: versions?.pocketbase?.[0],
          caddy: versions?.caddy?.[0],
          n8n: versions?.n8n?.[0],
        },
        options: {
          skip_onboarding: skipOnboarding,
          openrouter_budget_dollars: openrouterBudget,
        },
      }
      const provisionRes = await apiFetch(`/api/tenants/${tenant.id}/provision`, {
        method: 'POST',
        body: JSON.stringify({ wizard }),
      })
      if (!provisionRes.ok) { const err = await provisionRes.json().catch(() => ({ message: 'Failed to start provisioning' })); throw new Error(err.message ?? `Provision failed: ${provisionRes.status}`) }
      const provision = await provisionRes.json()
      setProvisionId(provision.job_id); setProvisionStartedAt(new Date().toISOString()); setCurrentStep(5)
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Provisioning failed') } finally { setIsSubmitting(false) }
  }

  const handleTeardown = async () => {
    if (!tenantId) return
    setIsTearingDown(true)
    try {
      const res = await apiFetch(`/api/tenants/${tenantId}/teardown`, {
        method: 'POST',
      })
      if (res.ok) {
        toast.success('Teardown job queued. Resources will be cleaned up.')
      } else {
        const err = await res.json().catch(() => ({ error: 'Teardown request failed' }))
        toast.error(err.error ?? 'Teardown failed')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Teardown request failed')
    } finally {
      setIsTearingDown(false)
    }
  }

  const provisionErrorMsg = useMemo(() => {
    if (!isProvisionFailed) return null
    const errorLog = [...logs].reverse().find(l => l.level === 'error' && l.message?.toLowerCase().includes('failed'))
    return errorLog?.message ?? 'Provisioning failed. Check logs for details.'
  }, [logs, isProvisionFailed])

  const canGoBack = currentStep > 1 && currentStep < 5
  const canClose = true  // Always allow closing — user can check progress from tenant detail later

  const validateStep3 = () => {
    // Domain login: blank = use defaults from env template (deconstraint.com,deconstraint.ai)
    // User login: must have at least one email
    if (authMode === 'user' && invitedEmails.length === 0) { toast.error('Please add at least one email address'); return false }
    return true
  }

  const handleNext = useCallback(async () => {
    if (currentStep === 1) { if (!(await step1Form.trigger())) return; setCurrentStep(2) }
    else if (currentStep === 2) { if (!(await step2Form.trigger())) return; setCurrentStep(3) }
    else if (currentStep === 3) { if (!validateStep3()) return; setCurrentStep(4) }
    else if (currentStep === 4) { if (!(await step4Form.trigger())) return; await startProvisioning() }
  }, [currentStep, step1Form, step2Form, step4Form, authMode, allowedDomain, invitedEmails])

  const handleBack = useCallback(() => { if (currentStep > 1 && currentStep < 5) setCurrentStep(s => s - 1) }, [currentStep])

  const stepTitle = STEP_LABELS[currentStep - 1] ?? 'Provision'

  return (
    <Modal isOpen={isOpen} onClose={canClose ? onClose : () => {}} title={`New Tenant \u2014 ${stepTitle}`} subtitle={currentStep < 5 ? `Step ${currentStep} of 5` : undefined} maxWidth="640px" footer={
      currentStep === 5 ? (
        <Button variant="primary" onClick={onClose} disabled={!isProvisionComplete && !isProvisionFailed}>{isProvisionComplete ? 'Done' : isProvisionFailed ? 'Close' : 'Provisioning...'}</Button>
      ) : (
        <>
          {canGoBack && <Button variant="ghost" icon={<ChevronLeft size={14} />} onClick={handleBack}>Back</Button>}
          <Button variant="primary" onClick={handleNext} disabled={isSubmitting || (currentStep === 1 && (slugStatus === 'taken' || slugStatus === 'checking'))}>
            {isSubmitting ? 'Starting...' : currentStep === 4 ? <><Zap size={14} /> Provision</> : <>Next <ChevronRight size={14} /></>}
          </Button>
        </>
      )
    }>
      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1; const isActive = stepNum === currentStep; const isCompleted = stepNum < currentStep
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {i > 0 && <div style={{ width: '24px', height: '1px', backgroundColor: isCompleted ? 'var(--brand)' : 'var(--border-subtle)' }} />}
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, backgroundColor: isActive || isCompleted ? 'var(--brand)' : 'var(--bg-tertiary)', color: isActive || isCompleted ? '#fff' : 'var(--text-tertiary)' }}>{stepNum}</div>
              <span style={{ fontSize: '12px', fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{label}</span>
            </div>
          )
        })}
      </div>

      {/* Step 1: Identity */}
      {currentStep === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNext() } }}>
          <FormField label="Legal Name" required error={step1Form.formState.errors.legal_name?.message}>
            <input className="input-field" placeholder="Acme Corporation" autoFocus {...step1Form.register('legal_name')} style={inputStyle} />
          </FormField>
          <FormField label="Slug" required hint="URL-safe identifier. Auto-generated from name." error={step1Form.formState.errors.slug?.message}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input className="input-field" placeholder="acme-corp" {...step1Form.register('slug', { onChange: () => setSlugManual(true) })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
              {slugManual && <Button variant="ghost" size="sm" type="button" onClick={() => { setSlugManual(false); step1Form.setValue('slug', toSlug(step1Form.getValues('legal_name')), { shouldValidate: true }) }}>Auto</Button>}
              {slugStatus === 'checking' && <Loader2 size={14} style={{ color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} />}
              {slugStatus === 'available' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--status-healthy)' }}><Check size={14} /> Available</span>}
              {slugStatus === 'taken' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--status-unhealthy)' }}><XCircle size={14} /> Already taken</span>}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: '4px' }}>{step1Form.watch('slug') ? `${step1Form.watch('slug')}.deconstraint.ai` : ''}</span>
          </FormField>
        </div>
      )}

      {/* Step 2: Contact */}
      {currentStep === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNext() } }}>
          <FormField label="Contact Name" required error={step2Form.formState.errors.primary_contact_name?.message}>
            <input className="input-field" placeholder="Jane Doe" autoFocus {...step2Form.register('primary_contact_name')} style={inputStyle} />
          </FormField>
          <FormField label="Contact Email" required error={step2Form.formState.errors.primary_contact_email?.message}>
            <input className="input-field" type="email" placeholder="jane@acme.com" {...step2Form.register('primary_contact_email')} style={inputStyle} />
          </FormField>
          <FormField label="Contact Phone" required error={step2Form.formState.errors.primary_contact_phone?.message}>
            <input className="input-field" type="tel" placeholder="(555) 123-4567" {...step2Form.register('primary_contact_phone', {
              onChange: (e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                if (digits.length <= 3) e.target.value = digits.length ? `(${digits}` : ''
                else if (digits.length <= 6) e.target.value = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                else e.target.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
                // Store raw digits in the form
                step2Form.setValue('primary_contact_phone', digits, { shouldValidate: true })
              }
            })} style={inputStyle} />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FormField label="Website" error={step2Form.formState.errors.website?.message}>
              <input className="input-field" placeholder="https://acme.com" {...step2Form.register('website')} style={inputStyle} />
            </FormField>
            <FormField label="Industry" error={step2Form.formState.errors.industry?.message}>
              <input className="input-field" placeholder="SaaS, Legal, etc." {...step2Form.register('industry')} style={inputStyle} />
            </FormField>
          </div>
        </div>
      )}

      {/* Step 3: Login Configuration */}
      {currentStep === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNext() } }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            Choose how users will log in to this tenant's workspace.
          </div>

          {/* Auth mode selection */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setAuthMode('domain')}
              style={{
                padding: '16px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                border: authMode === 'domain' ? '2px solid var(--brand)' : '1px solid var(--border-subtle)',
                backgroundColor: authMode === 'domain' ? 'var(--brand-dim, rgba(139,92,246,0.1))' : 'var(--bg-tertiary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center',
              }}
            >
              <Globe size={24} style={{ color: authMode === 'domain' ? 'var(--brand)' : 'var(--text-tertiary)' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>Domain Login</span>
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>All users with emails from a specific domain can log in via OAuth</span>
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('user')}
              style={{
                padding: '16px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                border: authMode === 'user' ? '2px solid var(--brand)' : '1px solid var(--border-subtle)',
                backgroundColor: authMode === 'user' ? 'var(--brand-dim, rgba(139,92,246,0.1))' : 'var(--bg-tertiary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center',
              }}
            >
              <UserPlus size={24} style={{ color: authMode === 'user' ? 'var(--brand)' : 'var(--text-tertiary)' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>User Invitations</span>
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Invite specific users by email address</span>
            </button>
          </div>

          {/* Domain input */}
          {authMode === 'domain' && (
            <FormField label="Allowed Domain" required hint="Users with this email domain can log in. E.g., 'acme.com'">
              <input
                className="input-field"
                placeholder="acme.com"
                autoFocus
                value={allowedDomain}
                onChange={e => setAllowedDomain(e.target.value.trim().toLowerCase())}
                style={inputStyle}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                Anyone with an @{allowedDomain || '...'} email can sign in via Google OAuth.
                Deconstraint domains are always included.
              </span>
            </FormField>
          )}

          {/* User invitation emails */}
          {authMode === 'user' && (
            <FormField label="Invite Users" required hint="Add email addresses of users who should have access.">
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="input-field"
                  placeholder="user@example.com"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); addInvitedEmail() } }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <Button variant="secondary" size="sm" type="button" onClick={addInvitedEmail}>Add</Button>
              </div>
              {invitedEmails.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {invitedEmails.map(email => (
                    <span key={email} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '4px 8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '12px',
                      fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
                    }}>
                      {email}
                      <button type="button" onClick={() => removeInvitedEmail(email)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, display: 'flex' }}>
                        <X size={12} style={{ color: 'var(--text-tertiary)' }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                These users will receive invitations. Deconstraint domains are always included.
              </span>
            </FormField>
          )}

          {/* Skip onboarding checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', marginTop: '4px' }}>
            <input
              type="checkbox"
              id="skip-onboarding"
              checked={skipOnboarding}
              onChange={e => setSkipOnboarding(e.target.checked)}
              style={{ accentColor: 'var(--brand)', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <label htmlFor="skip-onboarding" style={{ fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
              Skip onboarding for this tenant
            </label>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-8px' }}>
            When checked, users will skip the Jenny onboarding chat and go straight to the main app.
          </span>
        </div>
      )}

      {/* Step 4: Versions */}
      {currentStep === 4 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNext() } }}>
          {versionsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}><Skeleton height={40} /><Skeleton height={40} /></div>
          ) : (
            <>
              <FormField label="Web App Version" required error={step4Form.formState.errors.web_app_version?.message}>
                <select className="input-field" {...step4Form.register('web_app_version')} style={inputStyle}>
                  {versions?.webApp?.length === 0 && <option value="">No versions available</option>}
                  {versions?.webApp?.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </FormField>
              <FormField label="OpenClaw Version" required error={step4Form.formState.errors.openclaw_version?.message}>
                <select className="input-field" {...step4Form.register('openclaw_version')} style={inputStyle}>
                  {versions?.openclaw?.length === 0 && <option value="">No versions available</option>}
                  {versions?.openclaw?.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </FormField>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '1px', color: 'var(--text-tertiary)' }} />
                <div>
                  <div>PocketBase: <strong>{versions?.pocketbase?.[0] ?? <em style={{ color: 'var(--status-unhealthy)' }}>missing — registry unreachable</em>}</strong></div>
                  <div>Caddy: <strong>{versions?.caddy?.[0] ?? <em style={{ color: 'var(--status-unhealthy)' }}>missing — registry unreachable</em>}</strong></div>
                  <div>n8n: <strong>{versions?.n8n?.[0] ?? <em style={{ color: 'var(--status-unhealthy)' }}>missing — settings.default_n8n_tag not set</em>}</strong></div>
                  <div style={{ marginTop: '4px', color: 'var(--text-tertiary)' }}>These are managed automatically and cannot be changed per-tenant.</div>
                </div>
              </div>

              <FormField label="OpenRouter Monthly Budget (USD)" required>
                <input
                  type="number"
                  className="input-field"
                  min={1}
                  max={100000}
                  step={50}
                  value={openrouterBudget}
                  onChange={e => setOpenrouterBudget(Math.max(1, parseInt(e.target.value, 10) || 200))}
                  style={inputStyle}
                />
                <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                  Monthly spend cap for the tenant's OpenRouter key (named <code>client-{step1Form.watch('slug') || '{slug}'}</code>). Defaults to $200; raise for higher-throughput tenants.
                </div>
              </FormField>
            </>
          )}
        </div>
      )}

      {/* Step 5: Provisioning */}
      {currentStep === 5 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <MilestoneProgress logs={logs} startedAt={provisionStartedAt} completedAt={provisionCompletedAt} />
          <ProvisionLogViewer tenantId={tenantId} maxHeight="240px" />
          {isProvisionComplete && (
            <div style={{ padding: '12px 16px', backgroundColor: 'var(--status-healthy-dim)', border: '1px solid var(--status-healthy)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--status-healthy)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={16} /> Tenant provisioned successfully!
            </div>
          )}
          {isProvisionFailed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ padding: '12px 16px', backgroundColor: 'var(--status-unhealthy-dim)', border: '1px solid var(--status-unhealthy)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--status-unhealthy)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>Provisioning Failed</div>
                  <div style={{ opacity: 0.9 }}>{provisionErrorMsg}</div>
                </div>
              </div>
              <Button
                variant="danger"
                icon={<Trash2 size={14} />}
                onClick={handleTeardown}
                disabled={isTearingDown}
              >
                {isTearingDown ? 'Tearing Down...' : 'Teardown & Clean Up'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
